import {DashboardWidget} from './DashboardWidget';
import { useVISDashboard, getWidgets, VIS_DASHBOARD } from './useVISDashboard';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { annotateWidgetsWithPermission } from './permissionUtil';
import useUserSettingStore from './UserSettingStore';
import userProfileStore from '../core/UserProfileStore';


const styles = {
  container: {
    marginBottom: '16px',
  },
  flexWrapper: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '16px',
    alignItems: 'stretch',
  },
  largeWidget: {
    flex: '1 1 420px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  extraLargeWidget: {
    flex: '1 0 766px',
    display: 'flex',
    maxWidth: '766px',
    height: '360px',
    flexDirection: 'column' as const,
  },
  standardWidget: {
    flex: '1 1 280px',
    maxWidth: '520px',
  },
  cardWidget: {
    flex: '1 1 220px',
    maxWidth: '520px',
    display: 'flex',
    gap: '32px',
    flexDirection: 'column' as const,
  },
  emptyState: {
    padding: '24px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#4b5563',
    fontSize: '14px',
  },
};


export const useDashboardRegistry = () => {
  const { t } = useTranslation();
  const { dashboardAccessSetting } = useUserSettingStore();
  const { permissions } = userProfileStore();
  
  const visPermissionWidgets = useMemo(() => {
    return annotateWidgetsWithPermission(
      getWidgets(t),
      VIS_DASHBOARD.DOMAIN_ID,
      dashboardAccessSetting,
      permissions
    );
  }, [dashboardAccessSetting, permissions, t]);

  const visDashboard = useVISDashboard({
    enabled: Array.isArray(visPermissionWidgets) && visPermissionWidgets.some((w) => w.hasPermission),
    filteredWidgets: visPermissionWidgets,
  });

  const dashboards = [
    { id: VIS_DASHBOARD.DASHBOARD_ID, ...visDashboard },
  ] as any;

  return {
    visDashboard,
    getWidgetState: (dashboardId:string, widgetId:string) => {
      const dashboard = dashboards.find((d:any) => d.id === dashboardId);
      const widget = dashboard.widgets?.find((w:any) => w.id === widgetId);
      return {
        isLoading: dashboard.isLoading,
        error: dashboard.error,
        widgetData: widget ? dashboard.getWidgetData(widget) : null,
        widget: widget
      };
    }
  };
  
};

export const VISDashboard = () => {
  const { getWidgetState, visDashboard } = useDashboardRegistry();
  const { t } = useTranslation();
  
  const visWidgets = visDashboard?.widgets || [];
  const shouldShowEmptyState = !visDashboard?.isLoading && visWidgets.length === 0;
  
  const renderDashboardWidget = (widgetId: string, loaderCount?: number) => {
    const dashboardWidget = visWidgets.find((w: any) => w.id === widgetId);
    if (!dashboardWidget) {
      return null;
    }

    return (
      <DashboardWidget
        state={getWidgetState(VIS_DASHBOARD.DASHBOARD_ID, widgetId)}
        loaderCount={loaderCount}
      />
    );
  };

  const costPriceTrendWidget = renderDashboardWidget(VIS_DASHBOARD.COST_PRICE_TREND_ID);
  const appraisalInsightWidget = renderDashboardWidget(VIS_DASHBOARD.APPRAISAL_INSIGHT_ID);
  const appraisalPercentageWidget = renderDashboardWidget(VIS_DASHBOARD.APPRAISAL_PERCENTAGE_ID,2);
  const lookToBookWidget = renderDashboardWidget(VIS_DASHBOARD.LOOK_TO_BOOK_ID, 2);
  const usedInvAgingWidget = renderDashboardWidget(VIS_DASHBOARD.USED_INV_AGING_ID);
  const newInvAgingWidget = renderDashboardWidget(VIS_DASHBOARD.NEW_INV_AGING_ID);
  const mdsTrendWidget = renderDashboardWidget(VIS_DASHBOARD.MDS_TREND_ID);

  return (
    <>
    {shouldShowEmptyState && (
      <div style={styles.emptyState}>
        {t('dashboard.insufficientPermissions', 
          'This chart is unavailable as you do not have required permissions.')}
      </div>
    )}
      <div style={styles.container}>
        <div style={styles.flexWrapper}>
          {costPriceTrendWidget && 
            <div style={{ ...styles.standardWidget }}>
              {costPriceTrendWidget}
            </div>
          }
          {appraisalInsightWidget &&
            <div style={{ ...styles.standardWidget }}>
              {appraisalInsightWidget}
            </div>
          }
          <div style={{ ...styles.cardWidget }}>
            {appraisalPercentageWidget}
            {lookToBookWidget}
          </div>
        </div>
      </div>

        <div style={styles.container}>
            <div style={styles.flexWrapper}>
              {usedInvAgingWidget &&
              <div style={{ ...styles.standardWidget }}>
                  {usedInvAgingWidget}
              </div>
              }
              {newInvAgingWidget &&
              <div style={{ ...styles.standardWidget }}>
                  {newInvAgingWidget}
              </div>
            }
              {mdsTrendWidget &&
              <div style={{ ...styles.standardWidget }}>
                  {mdsTrendWidget}
              </div>
             }
            </div>
        </div>
    </>
  );
};
